<template>
  <b-card
    v-if="data"
    no-body
    class="monthly-delivery-charge-card"
  >
    <b-card-header class="monthly-delivery-charge-header">
      <h4 class="chart-title mb-0">
        {{ title }}
      </h4>
      <div class="header-actions">
        <small
          v-if="lastUpdatedAt"
          class="timestamp-badge"
          :class="{ 'clickable': onRefresh }"
          @click="handleRefresh"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="12"
            class="mr-1"
            :class="{ 'spin': isRefreshing }"
          />
          <span v-if="lastRefreshTimeAgo">
            {{ lastRefreshTimeAgo }}
          </span>
          <span v-else>
            {{ __dateTimeFormatterPassedTime(lastUpdatedAt) }}
          </span>
        </small>
      </div>
    </b-card-header>
    <b-card-body class="monthly-delivery-charge-body">
      <e-charts
        ref="monthly-delivery-charge-chart"
        autoresize
        :options="chartOptions"
        class="monthly-delivery-charge-echart"
        auto-resize
      />
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody,
} from 'bootstrap-vue'
import ECharts from 'vue-echarts'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/grid'
import 'echarts/lib/chart/line'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    'e-charts': ECharts,
    BCard,
    BCardHeader,
    BCardBody,
  },
  directives: {
    Ripple,
  },
  props: {
    title: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    data: {
      type: Object,
      default: () => {
      },
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    onRefresh: {
      type: Function,
      default: null,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
    themeColors: {
      type: Object,
      default: () => ({
        theme_color: '#7367f0',
        gradientColor1: '#7367f0',
        gradientColor2: '#00cfe8',
        gradientDirection: 'to right',
      }),
    },
  },
  data() {
    return {
      revenue_report: {},
      currentTime: Date.now(),
      updateInterval: null,
    }
  },
  computed: {
    lastRefreshTimeAgo() {
      try {
        const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
        const lastRefreshTime = refreshTimes.monthlydeliverycharge
        if (!lastRefreshTime) return null

        const lastRefresh = new Date(lastRefreshTime).getTime()
        const now = this.currentTime
        const diff = now - lastRefresh

        if (diff < 0) return null

        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)
        const months = Math.floor(days / 30)
        const years = Math.floor(months / 12)

        if (minutes < 1) return 'just now'
        if (minutes < 60) return `${minutes} minutes ago`
        if (hours < 24) return `${hours} hours ago`
        if (days < 30) return `${days} days ago`
        if (months < 12) return `${months} months ago`
        return `${years} years ago`
      } catch (e) {
        return null
      }
    },
    formatCategories() {
      const categories = this.data?.categories?.lables || []
      const monthMap = {
        January: 'Jan',
        February: 'Feb',
        March: 'Mar',
        April: 'Apr',
        May: 'May',
        June: 'Jun',
        July: 'Jul',
        August: 'Aug',
        September: 'Sep',
        October: 'Oct',
        November: 'Nov',
        December: 'Dec',
      }
      return categories.map(cat => {
        if (typeof cat === 'string') {
          // Handle format like "2025 February" or "2024 December" or "February 2025"
          const parts = cat.trim().split(' ')
          if (parts.length >= 2) {
            const [firstPart, ...restParts] = parts
            const lastPart = parts[parts.length - 1]
            let year
            let month
            // Check if year is first (format: "2025 February")
            if (/^\d{4}$/.test(firstPart)) {
              year = firstPart
              month = restParts.join(' ')
            } else if (/^\d{4}$/.test(lastPart)) {
              // Check if year is last (format: "December 2024")
              year = lastPart
              month = parts.slice(0, -1).join(' ')
            } else {
              return { month: cat, year: '' }
            }
            const shortMonth = monthMap[month] || month.substring(0, 3)
            return { month: shortMonth, year }
          }
          return { month: cat, year: '' }
        }
        return { month: cat, year: '' }
      })
    },
    chartOptions() {
      const categories = this.formatCategories
      const seriesData = this.data?.series || []

      // Format series data
      const formattedSeries = seriesData.map((series, index) => {
        // Map colors based on series name: Delivery = green, Partially = blue, Return = orange-red
        const seriesName = (series.name || '').toLowerCase()
        let color = '#6E6B7B' // default gray

        if (seriesName.includes('delivery')) {
          color = '#28c76f' // green
        } else if (seriesName.includes('partial')) {
          color = '#00d4ff' // blue
        } else if (seriesName.includes('return')) {
          color = '#ff7043' // orange-red
        } else {
          // Fallback to index-based colors if name doesn't match
          const fallbackColors = ['#28c76f', '#00d4ff', '#ff7043'] // green, blue, orange-red
          color = fallbackColors[index] || fallbackColors[0]
        }

        // Ensure data is properly formatted as numbers
        const formattedData = (series.data || []).map(val => {
          const numVal = typeof val === 'string' ? parseFloat(val) : val
          return Number.isNaN(numVal) ? 0 : numVal
        })

        return {
          name: series.name || `Series ${index + 1}`,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color,
            width: 3,
          },
          itemStyle: {
            color,
          },
          data: formattedData,
        }
      })

      // Calculate y-axis range from all series
      const allValues = formattedSeries.flatMap(s => s.data)
      const maxVal = allValues.length > 0 ? Math.max(...allValues) : 16000
      const yAxisMin = 0
      const yAxisMax = Math.ceil(maxVal / 4000) * 4000 // Round up to nearest 4000

      return {
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            let result = ''
            if (params && params.length > 0) {
              const category = categories[params[0].dataIndex]
              result = `${category.month} ${category.year}<br/>`
              params.forEach(param => {
                const value = param.value.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                result += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>${param.seriesName}: ${value}<br/>`
              })
            }
            return result
          },
          backgroundColor: '#fff',
          borderColor: '#E4E6EA',
          borderWidth: 1,
          textStyle: {
            color: '#5e5873',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12,
          },
        },
        legend: {
          show: true,
          top: 0,
          left: 'center',
          textStyle: {
            color: '#6E6B7B',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12,
          },
        },
        grid: {
          left: '40px',
          right: '20px',
          top: '40px',
          bottom: '60px',
          containLabel: false,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: categories.map(cat => `${cat.month}\n${cat.year}`),
          axisLine: {
            show: true,
            lineStyle: {
              color: '#e0e0e0',
            },
          },
          axisTick: {
            show: true,
            lineStyle: {
              color: '#e0e0e0',
            },
            length: 5,
          },
          axisLabel: {
            color: '#6E6B7B',
            fontSize: 12,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
            align: 'center',
            lineHeight: 16,
            margin: 12,
          },
        },
        yAxis: {
          type: 'value',
          scale: false,
          min: yAxisMin,
          max: yAxisMax,
          splitNumber: 4,
          splitLine: {
            show: true,
            lineStyle: {
              color: '#f1f1f1',
              type: 'dashed',
              width: 1,
            },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            color: '#6E6B7B',
            fontSize: 12,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
            formatter(value) {
              const numValue = Number(value)
              if (Number.isNaN(numValue)) return '0'
              return Math.round(numValue).toLocaleString()
            },
          },
        },
        series: formattedSeries,
      }
    },
  },
  watch: {
    data: {
      handler() {
        // Force chart update when data changes
        if (this.$refs['monthly-delivery-charge-chart']) {
          this.$refs['monthly-delivery-charge-chart'].resize()
        }
      },
      deep: true,
    },
  },
  mounted() {
    // Update countdown every second
    this.updateInterval = setInterval(() => {
      this.currentTime = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    handleRefresh() {
      if (this.onRefresh && !this.isRefreshing) {
        this.onRefresh()
      }
    },
  },
}
</script>

<style scoped>
.monthly-delivery-charge-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border-radius: 0.857rem;
  overflow: hidden;
  height: 100%;
  transition: all 0.3s ease;
}

.monthly-delivery-charge-card:hover {
  box-shadow: 0 8px 32px 0 rgba(34, 41, 47, 0.15);
  transform: translateY(-2px);
}

.monthly-delivery-charge-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #fff;
}

.chart-title {
  font-size: 1.285rem;
  font-weight: 600;
  color: #5e5873;
  margin: 0;
  font-family: 'Roboto', sans-serif;
}

.header-actions {
  display: flex;
  align-items: center;
}

.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  color: #6e6b7b;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
}

.timestamp-badge.clickable {
  cursor: pointer;
  user-select: none;
}

.timestamp-badge.clickable:hover {
  background-color: #e8e8e8;
  transform: scale(1.05);
}

.timestamp-badge.clickable:active {
  transform: scale(0.98);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.monthly-delivery-charge-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #fff;
}

.monthly-delivery-charge-echart {
  width: 100%;
  height: 280px;
}

@media (max-width: 768px) {
  .monthly-delivery-charge-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .monthly-delivery-charge-echart {
    height: 250px;
  }
}
</style>
